import React from 'react';

type DynamicJSONCardProps = {
  header: string;
  content: any;
};

const DynamicJSONCard = ({ header, content }: DynamicJSONCardProps) => {
  // Helper to render array values as individual tags
  const renderArrayAsTags = (array: any[]) => (
    <span className="flex inline-flex flex-wrap gap-1">
      {array.map((item, i) => (
        <span key={i} className="rounded bg-teal-t10 px-2 py-1 font-normal">
          {item}
        </span>
      ))}
    </span>
  );

  // Helper to render boolean values as checkboxes
  const renderBoolean = (value: boolean) => (
    <input
      type="checkbox"
      checked={value}
      readOnly
      className="mb-1 h-4 w-4 cursor-default self-end accent-unlisted-secondary-gray"
    />
  );

  // Function to render content based on its type
  const renderContent = (data: any) => {
    if (typeof data === 'object' && data !== null) {
      return (
        <div className="w-full">
          {Object.entries(data).map(([key, value], index, arr) => (
            <div
              key={index}
              className={`py-3 ${index !== arr.length - 1 ? 'border-b border-unlisted-gray-lines' : ''}`}
            >
              {Array.isArray(value) ? (
                <div className="flex items-baseline">
                  <span className="mr-2 font-medium">
                    {key.charAt(0).toUpperCase() + key.slice(1)}
                  </span>
                  {renderArrayAsTags(value)}
                </div>
              ) : typeof value === 'object' && value !== null ? (
                <div>
                  <div className="font-medium">
                    {key.charAt(0).toUpperCase() + key.slice(1)}
                  </div>
                  <div className="ml-4 mt-2 w-full">
                    {Object.entries(value as object).map(
                      ([subKey, subValue], subIndex, subArr) => (
                        <div
                          key={subIndex}
                          className={`pb-2 ${subIndex !== subArr.length - 1 ? 'mb-2 border-b border-unlisted-gray-lines' : ''}`}
                        >
                          {typeof subValue === 'boolean' ? (
                            <div className="flex items-end">
                              {renderBoolean(subValue)}
                              <span className="ml-2 font-medium">
                                {subKey.charAt(0).toUpperCase() +
                                  subKey.slice(1)}
                              </span>
                            </div>
                          ) : (
                            <>
                              <span className="mr-2 font-medium">
                                {subKey.charAt(0).toUpperCase() +
                                  subKey.slice(1)}
                              </span>
                              {Array.isArray(subValue) ? (
                                renderArrayAsTags(subValue)
                              ) : (
                                <span className="font-semibold text-ui-cta-primary">
                                  {String(subValue)}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      ),
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex w-full items-start">
                  {typeof value === 'boolean' ? (
                    <>
                      {renderBoolean(value)}
                      <span className="ml-2 font-medium">
                        {key.charAt(0).toUpperCase() + key.slice(1)}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="mr-2 self-start font-medium">
                        {key.charAt(0).toUpperCase() + key.slice(1)}
                      </span>
                      <span className="self-start font-semibold text-ui-text-primary-text">
                        {String(value)}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      );
    } else if (Array.isArray(data)) {
      return <div className="w-full">{renderArrayAsTags(data)}</div>;
    } else {
      return typeof data === 'boolean' ? (
        renderBoolean(data)
      ) : (
        <p className="w-full font-semibold text-ui-cta-primary">
          {String(data)}
        </p>
      );
    }
  };

  return (
    <div className="w-full">
      <div className="relative mb-3 mt-4 flex w-full flex-col items-start rounded border border-unlisted-gray-lines bg-unlisted-light-gray px-4 py-3">
        <div className="w-full">
          <label className="text-lg font-semibold text-ui-text-primary-text">
            {header}
          </label>
        </div>
        <div className="mt-2 flex w-full flex-col items-start justify-start">
          {renderContent(content)}
        </div>
      </div>
    </div>
  );
};

export default DynamicJSONCard;
